import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { LoginRequest, RegisterRequest, SessionInfo } from '@scada-ink/shared/cloud';
import { cloudApi } from './cloudApi';

interface SessionState {
  session: SessionInfo | null;
  /** True while a session check is in flight. */
  loading: boolean;
  login: (body: LoginRequest) => Promise<void>;
  register: (body: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Check the cookie with the cloud once, if nothing has yet. For pages that need an answer. */
  verify: () => void;
}

const SessionContext = createContext<SessionState | null>(null);

/**
 * A hint that this browser has signed in before. The session itself is an
 * httpOnly cookie the page cannot read, so without the hint every anonymous
 * visit to any marketing page would ask the cloud "am I signed in?" and get a
 * 401. With it, only returning customers — and the login and portal pages,
 * which always verify — make that call.
 */
const HINT_KEY = 'si_signed_in';

function readHint(): boolean {
  try {
    return localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHint(on: boolean) {
  try {
    if (on) localStorage.setItem(HINT_KEY, '1');
    else localStorage.removeItem(HINT_KEY);
  } catch {
    // Storage blocked: the hint is only an optimisation.
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(readHint);
  const attempted = useRef(false);

  const apply = useCallback((next: SessionInfo | null) => {
    setSession(next);
    writeHint(!!next);
  }, []);

  const refresh = useCallback(async () => {
    attempted.current = true;
    setLoading(true);
    try {
      apply(await cloudApi.get<SessionInfo>('/api/portal/auth/me'));
    } catch {
      // Signed out, or the cloud is unreachable. Either way the marketing site
      // carries on as a visitor.
      apply(null);
    } finally {
      setLoading(false);
    }
  }, [apply]);

  const verify = useCallback(() => {
    if (!attempted.current) void refresh();
  }, [refresh]);

  useEffect(() => {
    if (readHint()) void refresh();
  }, [refresh]);

  const value = useMemo<SessionState>(
    () => ({
      session,
      loading,
      refresh,
      verify,
      login: async (body) => apply(await cloudApi.post<SessionInfo>('/api/portal/auth/login', body)),
      register: async (body) => apply(await cloudApi.post<SessionInfo>('/api/portal/auth/register', body)),
      logout: async () => {
        try {
          await cloudApi.post('/api/portal/auth/logout');
        } finally {
          apply(null);
        }
      },
    }),
    [session, loading, refresh, verify, apply],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
